/**
 * Builder helpers for page content maps.
 *
 * PAWTENANT-SEO-EDITOR-V3-NOCODE-WORKSPACE-001
 *
 * A page's content.ts reads like the page: sections in order, fields in
 * order, defaults inline. The helpers derive stable ids ("<section>.<key>"),
 * fill in the kind-appropriate limits and refuse duplicates at module load,
 * so a mistake in a map is a build error rather than a runtime surprise.
 */

import type {
  SeoCollectionColumn,
  SeoCollectionKind,
  SeoEditableCollection,
  SeoEditableField,
  SeoEditableSection,
  SeoFieldConstraints,
  SeoFieldKind,
  SeoPageContentMap,
  SeoSectionType,
} from "./types";
import { VISIBILITY_FIELD_SUFFIX, VISIBILITY_VALUES } from "./types";

/** Kind-appropriate defaults. A map may tighten them; it cannot loosen the hard ceilings. */
export const KIND_LIMITS: Record<SeoFieldKind, { maxLength: number; recommended?: [number, number] }> = {
  eyebrow: { maxLength: 60, recommended: [8, 40] },
  heading: { maxLength: 120, recommended: [20, 70] },
  paragraph: { maxLength: 1200, recommended: [60, 320] },
  list_item: { maxLength: 200, recommended: [12, 110] },
  cta_label: { maxLength: 40, recommended: [8, 28] },
  cta_url: { maxLength: 200 },
  faq_question: { maxLength: 180, recommended: [20, 110] },
  faq_answer: { maxLength: 900, recommended: [80, 420] },
  image: { maxLength: 300 },
  image_alt: { maxLength: 160, recommended: [30, 125] },
  badge: { maxLength: 40, recommended: [4, 24] },
  variant: { maxLength: 40 },
  toggle: { maxLength: 10 },
};

/** Destinations a call-to-action button may point at unless a field narrows the list. */
export const DEFAULT_CTA_ROUTES = [
  "/assessment",
  "/psd-assessment",
  "/esa-letter-cost",
  "/how-to-get-esa-letter",
  "/faqs",
  "/about-us",
  "/contact-us",
  "/housing-rights-esa",
  "/esa-letter-housing",
  "/our-providers",
  "/resource-center",
  "/blog",
];

type FieldSpec = {
  kind: SeoFieldKind;
  label: string;
  value: string;
  where?: string;
  help?: string;
  editable?: boolean;
  managed?: boolean;
  constraints?: SeoFieldConstraints;
};

export type FieldSpecs = Record<string, FieldSpec>;

function spec(kind: SeoFieldKind, label: string, value: string, extra: Partial<FieldSpec> = {}): FieldSpec {
  return { kind, label, value, ...extra };
}

/** Terse constructors, one per kind. */
export const f = {
  eyebrow: (label: string, value: string, x: Partial<FieldSpec> = {}) => spec("eyebrow", label, value, x),
  heading: (label: string, value: string, x: Partial<FieldSpec> = {}) => spec("heading", label, value, x),
  paragraph: (label: string, value: string, x: Partial<FieldSpec> = {}) => spec("paragraph", label, value, x),
  ctaLabel: (label: string, value: string, x: Partial<FieldSpec> = {}) => spec("cta_label", label, value, x),
  ctaUrl: (label: string, value: string, allowed: string[] = DEFAULT_CTA_ROUTES, x: Partial<FieldSpec> = {}) =>
    spec("cta_url", label, value, { ...x, constraints: { allowedRoutes: allowed, ...(x.constraints ?? {}) } }),
  image: (label: string, value: string, x: Partial<FieldSpec> = {}) => spec("image", label, value, x),
  imageAlt: (label: string, value: string, x: Partial<FieldSpec> = {}) => spec("image_alt", label, value, x),
  badge: (label: string, value: string, x: Partial<FieldSpec> = {}) => spec("badge", label, value, x),
  variant: (label: string, value: string, allowedValues: string[], x: Partial<FieldSpec> = {}) =>
    spec("variant", label, value, { ...x, constraints: { allowedValues, ...(x.constraints ?? {}) } }),
  /** A value the page shows but this editor cannot change (price, legal line, shared component). */
  managed: (label: string, value: string, x: Partial<FieldSpec> = {}) =>
    spec("paragraph", label, value, { ...x, editable: false, managed: true }),
};

/** A collection column, e.g. col("question", "Question", "faq_question"). */
export function col(key: string, label: string, kind: SeoFieldKind, x: Partial<SeoCollectionColumn> = {}): SeoCollectionColumn {
  return { key, label, kind, ...x };
}

export interface CollectionSpec {
  label: string;
  kind: SeoCollectionKind;
  columns: SeoCollectionColumn[];
  items: Array<Record<string, string>>;
  reorderable?: boolean;
  addable?: boolean;
  removable?: boolean;
  minItems?: number;
  maxItems?: number;
  where?: string;
  help?: string;
}

/** Ready-made FAQ collection. */
export function faqCollection(items: Array<{ q: string; a: string }>, x: Partial<CollectionSpec> = {}): CollectionSpec {
  return {
    label: "Questions and answers",
    kind: "faq",
    columns: [col("question", "Question", "faq_question"), col("answer", "Answer", "faq_answer")],
    items: items.map((i) => ({ question: i.q, answer: i.a })),
    reorderable: true, addable: true, removable: true, minItems: 1, maxItems: 30,
    where: "The FAQ accordion, and the FAQPage structured data search engines read.",
    help: "Visible questions and the structured data come from this one list, so they can never disagree.",
    ...x,
  };
}

/** Ready-made bullet list. */
export function bulletCollection(label: string, items: string[], x: Partial<CollectionSpec> = {}): CollectionSpec {
  return {
    label,
    kind: "bullets",
    columns: [col("text", "Bullet", "list_item")],
    items: items.map((text) => ({ text })),
    reorderable: true, addable: true, removable: true, minItems: 1, maxItems: 12,
    ...x,
  };
}

/** A keyed card set: text can change and cards can be re-ordered; cards cannot be added or removed. */
export function cardCollection(label: string, columns: SeoCollectionColumn[], items: Array<Record<string, string>>, x: Partial<CollectionSpec> = {}): CollectionSpec {
  return {
    label,
    kind: "cards",
    columns,
    items,
    reorderable: true, addable: false, removable: false, minItems: items.length, maxItems: items.length,
    ...x,
  };
}

export interface SectionSpec {
  id: string;
  label: string;
  sectionType: SeoSectionType;
  description?: string;
  fields?: FieldSpecs;
  collections?: Record<string, CollectionSpec>;
  /** Editors may hide/show this section (never the hero). */
  hideable?: boolean;
  /** A shared design component with nothing editable. */
  managed?: boolean;
}

function fieldFrom(sectionId: string, key: string, s: FieldSpec): SeoEditableField {
  const limits = KIND_LIMITS[s.kind];
  const editable = s.editable ?? !s.managed;
  const constraints: SeoFieldConstraints = {
    maxLength: Math.min(s.constraints?.maxLength ?? limits.maxLength, limits.maxLength),
    ...(s.constraints?.minLength ? { minLength: s.constraints.minLength } : {}),
    ...(s.constraints?.allowedRoutes ? { allowedRoutes: s.constraints.allowedRoutes } : {}),
    ...(s.constraints?.allowedValues ? { allowedValues: s.constraints.allowedValues } : {}),
    ...((s.constraints?.recommended ?? limits.recommended) ? { recommended: s.constraints?.recommended ?? limits.recommended } : {}),
  };
  return {
    id: `${sectionId}.${key}`,
    label: s.label,
    kind: s.kind,
    value: s.value,
    editable,
    source: s.managed ? "managed_design" : "registry",
    where: s.where,
    help: s.help,
    constraints,
  };
}

function collectionFrom(sectionId: string, key: string, c: CollectionSpec): SeoEditableCollection {
  const keyed = c.columns.some((x) => x.key === "key");
  const columns = c.columns.map((x) => {
    const limits = KIND_LIMITS[x.kind];
    return {
      ...x,
      constraints: {
        maxLength: Math.min(x.constraints?.maxLength ?? limits.maxLength, limits.maxLength),
        ...(x.constraints?.allowedRoutes ? { allowedRoutes: x.constraints.allowedRoutes } : {}),
        ...(x.constraints?.allowedValues ? { allowedValues: x.constraints.allowedValues } : {}),
        ...((x.constraints?.recommended ?? limits.recommended) ? { recommended: x.constraints?.recommended ?? limits.recommended } : {}),
      },
    };
  });
  if (keyed) {
    const keys = c.items.map((i) => i.key);
    if (new Set(keys).size !== keys.length) throw new Error(`[seo/content] duplicate card key in ${sectionId}.${key}`);
  }
  return {
    id: `${sectionId}.${key}`,
    label: c.label,
    kind: c.kind,
    columns,
    items: c.items,
    reorderable: c.reorderable ?? true,
    addable: keyed ? false : (c.addable ?? true),
    removable: keyed ? false : (c.removable ?? true),
    minItems: c.minItems ?? (keyed ? c.items.length : 1),
    maxItems: c.maxItems ?? (keyed ? c.items.length : 30),
    keyed,
    where: c.where,
    help: c.help,
  };
}

export function definePageContent(routePath: string, version: number, sections: SectionSpec[]): SeoPageContentMap {
  const seen = new Set<string>();
  const out: SeoEditableSection[] = sections.map((s, i) => {
    if (seen.has(s.id)) throw new Error(`[seo/content] duplicate section id ${s.id} on ${routePath}`);
    seen.add(s.id);
    const fields: SeoEditableField[] = Object.entries(s.fields ?? {}).map(([k, v]) => fieldFrom(s.id, k, v));
    if (s.hideable) {
      fields.push({
        id: `${s.id}${VISIBILITY_FIELD_SUFFIX}`,
        label: "Section visibility",
        kind: "toggle",
        value: "visible",
        editable: true,
        source: "registry",
        where: "Whether this whole section appears on the page.",
        help: "Hidden sections are removed from the page and from search engines' view.",
        constraints: { maxLength: KIND_LIMITS.toggle.maxLength, allowedValues: [...VISIBILITY_VALUES] },
      });
    }
    const collections = Object.entries(s.collections ?? {}).map(([k, v]) => collectionFrom(s.id, k, v));
    return {
      id: s.id,
      label: s.label,
      order: i + 1,
      sectionType: s.sectionType,
      description: s.description,
      fields,
      collections,
      managed: s.managed === true,
    };
  });
  const ids = out.flatMap((s) => [...s.fields.map((x) => x.id), ...s.collections.map((x) => x.id)]);
  if (new Set(ids).size !== ids.length) throw new Error(`[seo/content] duplicate field id on ${routePath}`);
  return { routePath, version, sections: out };
}
